import React, { useContext } from 'react';
import { Route } from 'react-router-dom';

import {
  GQLAddress,
  GQLMerchantChannelCollection,
  GQLParty
} from '@jewelutions/core-crm-server-api-client-domain';
import { GQLOperationBuilder } from '../../../operations/GQLOperationBuilder';
import OverallSettings from '../merchantChannels/OverallSettings';
import DeliverySettings from '../merchantChannels/DeliverySettings';
import LookAndFeelSettings from '../merchantChannels/LookAndFeelSettings';
import {
  MerchantChannelCollectionProductList,
  MerchantChannelCollectionSettings
} from '../merchantChannelCatalogs';
import { LoadingSpinner } from '../../../components';
import { MerchantNavigation } from './MerchantNavigation';
import { ShoppingCart, BookOpen, ShoppingBag } from 'react-feather';
import { RouteType } from '../../../types/routes';
import { Exception } from '@jewelutions/core-common-domain';
import { PlatformSaleCatalogDetails } from '../platformSaleCatalogs';
import PlatformMerchantOrdersList from '../platformMerchantOrders/PlatformMerchantOrdersList';
import PlatformMerchantOrderDetails from '../platformMerchantOrders/PlatformMerchantOrderDetails';
import MerchantChannelCatalogDetails from '../merchantChannelCatalogs/MerchantChannelCatalogDetails';
import Support from '../support/Support';
import MerchantChannelCatalogPreview from '../merchantChannelCatalogs/MerchantChannelCatalogPreview/MerchantChannelCatalogPreview';
import { AuthContext } from '../../../core/AuthContext';
import { DefaultLayout } from '../../../types-hack';

interface MerchantRoutesProps {}

const MerchantRoutes: React.FC<MerchantRoutesProps> = ({}: MerchantRoutesProps) => {
  const securityContext = useContext(AuthContext).auth!;

  const party: GQLParty = {
    id: securityContext.partyId,
    name: securityContext.partyName,
    type: securityContext.partyType,
    email: securityContext.userEmail,
    address: (undefined as unknown) as GQLAddress
  };

  const {
    data: merchantChannelAndCatalogsData,
    loading: merchantChannelAndCatalogLoading
  } = GQLOperationBuilder.party.getPartyMerchantChannelAndMerchantChannelCatalogs({ partyId: party.id });

  if (merchantChannelAndCatalogLoading) return <LoadingSpinner />;

  const {
    merchantChannel,
    merchantChannelCatalog
  } = GQLOperationBuilder.party.merchantChannelAndMerchantChannelCatalogArrayFromResponse(
    merchantChannelAndCatalogsData
  )[0];

  if (merchantChannelCatalog === undefined || merchantChannelCatalog === null)
    throw new Exception(`Cannot load merchant. Merchant Channel catalog missing.`);
  const collections: GQLMerchantChannelCollection[] = merchantChannelCatalog.availableCollections;

  const sidebarRoutes: RouteType[] = [
    {
      id: 'Shop Settings',
      path: '/settings',
      icon: <ShoppingBag />,
      ...(merchantChannel.domain && { header: merchantChannel.domain }),
      children: [
        {
          path: MerchantNavigation.merchantChannelSettingsOverall.pathFromParams({
            merchantChannelId: merchantChannel.id
          }),
          name: 'Overall'
        },
        {
          path: MerchantNavigation.merchantChannelSettingsDelivery.pathFromParams({
            merchantChannelId: merchantChannel.id
          }),
          name: 'Delivery'
        },
        {
          path: MerchantNavigation.merchantChannelSettingsLookAndFeel.pathFromParams({
            merchantChannelId: merchantChannel.id
          }),
          name: 'Look & Feel'
        }
      ]
    },
    {
      id: 'Orders & pricing',
      path: MerchantNavigation.platformMerchantOrderList.pathFromParams({
        merchantChannelId: merchantChannel.id
      }),
      icon: <ShoppingCart />,
      children: [
        {
          path: MerchantNavigation.platformMerchantOrderList.pathFromParams({
            merchantChannelId: merchantChannel.id
          }),
          name: 'Shop orders'
        },
        {
          path: MerchantNavigation.platformSaleCatalogDetails.pathFromParams({}),
          name: 'Platform contract'
        }
      ]
    }
  ].concat(
    collections.map(collection => {
      return {
        id: `Product catalog`,
        path: MerchantNavigation.merchantChannelCatalogDetails.pathFromParams({
          merchantChannelCatalogId: merchantChannelCatalog.id
        }),
        icon: <BookOpen />,
        children: [
          {
            path: MerchantNavigation.merchantChannelCatalogDetails.pathFromParams({
              merchantChannelCatalogId: merchantChannelCatalog.id
            }),
            name: `Catalog overview`
          },
          {
            path: MerchantNavigation.merchantChannelCatalogCollectionProductList.pathFromParams({
              merchantChannelCatalogId: merchantChannelCatalog.id,
              collectionId: collection.id
            }),
            name: 'Primary collection'
          }
        ]
      };
    })
  );

  return (
    <React.Fragment>
      <Route
        path={MerchantNavigation.merchantChannelSettingsOverall.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Overall Settings"
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<OverallSettings party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={MerchantNavigation.merchantChannelSettingsDelivery.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Delivery Settings"
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<DeliverySettings party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={MerchantNavigation.merchantChannelSettingsLookAndFeel.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Look&Feel Settings"
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<LookAndFeelSettings party={party} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={MerchantNavigation.platformMerchantOrderList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Orders"
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<PlatformMerchantOrdersList party={party} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={MerchantNavigation.platformMerchantOrderDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Order details"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<PlatformMerchantOrderDetails party={party} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={MerchantNavigation.platformSaleCatalogDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Platform sale contract"
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<PlatformSaleCatalogDetails party={party} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={MerchantNavigation.merchantChannelCatalogDetails.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Catalog overview"
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<MerchantChannelCatalogDetails party={party} merchantChannelId={merchantChannel.id} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={MerchantNavigation.merchantChannelCatalogPreview.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Catalog Preview"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<MerchantChannelCatalogPreview party={party} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={MerchantNavigation.merchantChannelCatalogCollectionSettings.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Catalog collection settings"
            hasBackButton
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<MerchantChannelCollectionSettings party={party} />}
          </DefaultLayout>
        )}
      />

      <Route
        path={MerchantNavigation.merchantChannelCatalogCollectionProductList.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Catalog collection products"
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<MerchantChannelCollectionProductList party={party} />}
          </DefaultLayout>
        )}
      />
      <Route
        path={MerchantNavigation.support.pathPattern()}
        exact
        render={() => (
          <DefaultLayout
            title="Support"
            sidebarRoutes={sidebarRoutes}
            helpRoute={MerchantNavigation.support.pathPattern()}
          >
            {<Support party={party} />}
          </DefaultLayout>
        )}
      />
    </React.Fragment>
  );
};
export default MerchantRoutes;
